import {
  ClockIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DashboardAlert, DashboardHandlingDeadline } from '../api/types';
import useDashboardAlerts from '../hooks/useDashboardAlerts';
import {
  DashboardToast,
  DashboardToastContext,
  DashboardToastTone,
} from '../hooks/useDashboardToast';
import { minutesUntil, startsIn } from '../util/countdown';

import ShiftDetailPanel from './ShiftDetailPanel';

// How long a toast stays up before it dismisses itself.
export const TOAST_TTL_MS = 10_000;
// The most toasts on screen at once; older ones give way.
const MAX_VISIBLE = 3;

const TONE_ICONS: Record<DashboardToastTone, typeof ClockIcon> = {
  info: InformationCircleIcon,
  warning: ClockIcon,
  critical: ExclamationCircleIcon,
};

// Figma 40567:190052 — broadcast to every admin in scope.
const alertToast = (alert: DashboardAlert): DashboardToast => {
  const span = startsIn(alert.starts_at);
  return {
    key: `alert:${alert.id}`,
    tone: 'critical',
    title: alert.title,
    message: `${alert.message}${span ? ` — starts in ${span}` : ''}.`,
    shiftId: alert.shift_id,
  };
};

// Figma 40567:189370 — to the holder only. Keyed on `nudge_at`, which moves
// forward whenever the holder acts, so a claim that goes idle again is nudged
// again, but one idle stretch is never nudged twice.
const nudgeToast = (h: DashboardHandlingDeadline): DashboardToast => ({
  key: `nudge:${h.job_id}:${h.nudge_at}`,
  tone: 'warning',
  title: `Still there? Shift #${h.job_id} needs action`,
  message: `No activity in 20 min — it'll return to the queue in ${minutesUntil(
    h.expires_at,
  )} min.`,
  shiftId: h.shift_id,
});

type Props = { agencyId: number | null; children: ReactNode };

/**
 * The dashboard's toast layer (GRAVY-10443): provides `notify`, polls the alert
 * feed, schedules the idle nudge, and draws the stack.
 *
 * Mounted once, above the tab switch, so a toast survives a tab change and the
 * feed polls whichever tab is open. Clicking a toast opens that shift's panel
 * from here, for the same reason — the shift may not be on the current tab.
 */
const DashboardToasts = ({ agencyId, children }: Props) => {
  const [toasts, setToasts] = useState<DashboardToast[]>([]);
  const [openShiftId, setOpenShiftId] = useState<number | null>(null);
  // Every key ever shown this session, so a re-render or a refetch that returns
  // the same alert does not raise it twice.
  const shown = useRef(new Set<string>());
  const timers = useRef(new Map<string, number>());

  const dismiss = useCallback((key: string) => {
    window.clearTimeout(timers.current.get(key));
    timers.current.delete(key);
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const notify = useCallback(
    (toast: DashboardToast) => {
      if (shown.current.has(toast.key)) return;
      shown.current.add(toast.key);
      setToasts((prev) => [...prev, toast].slice(-MAX_VISIBLE));
      timers.current.set(
        toast.key,
        window.setTimeout(() => dismiss(toast.key), TOAST_TTL_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((t) => window.clearTimeout(t));
  }, []);

  const { data } = useDashboardAlerts(agencyId);

  useEffect(() => {
    data?.alerts.forEach((alert) => notify(alertToast(alert)));
  }, [data?.alerts, notify]);

  // The nudge is a deadline, not an event, so it is scheduled here from
  // `nudge_at` rather than waited for from the server. Only inside the window
  // between nudge and release: past `expires_at` the claim is already gone.
  const handlings = data?.my_handlings;
  useEffect(() => {
    if (!handlings?.length) return undefined;
    const now = Date.now();
    const scheduled = handlings
      .filter((h) => new Date(h.expires_at).getTime() > now)
      .map((h) =>
        window.setTimeout(
          () => notify(nudgeToast(h)),
          Math.max(new Date(h.nudge_at).getTime() - now, 0),
        ),
      );
    return () => scheduled.forEach((t) => window.clearTimeout(t));
  }, [handlings, notify]);

  const context = useMemo(() => ({ notify }), [notify]);

  return (
    <DashboardToastContext.Provider value={context}>
      {children}

      {toasts.length > 0 && (
        <div className="dashv2-toasts" role="status" aria-live="polite">
          {toasts.map((toast) => {
            const Icon = TONE_ICONS[toast.tone];
            const open = () => {
              dismiss(toast.key);
              if (toast.shiftId != null) setOpenShiftId(toast.shiftId);
            };
            return (
              <button
                key={toast.key}
                type="button"
                className={`dashv2-toast is-${toast.tone}`}
                data-testid="dashboardToast"
                onClick={open}
              >
                <Icon className="dashv2-toast-icon" aria-hidden="true" />
                <span className="dashv2-toast-text">
                  <span className="dashv2-toast-title">{toast.title}</span>{' '}
                  <span className="dashv2-toast-message">{toast.message}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {agencyId != null && openShiftId != null && (
        <ShiftDetailPanel
          agencyId={agencyId}
          shiftId={openShiftId}
          onClose={() => setOpenShiftId(null)}
        />
      )}
    </DashboardToastContext.Provider>
  );
};

export default DashboardToasts;
